import {UrlUtility} from "./UrlUtility";
import {GenericModel} from "./hooks/GenericModel";
import {FpInvoice, FpService, Invoice} from "../invoices/InvoiceTypes";
import {Subscription} from "../subscriptions/SubscriptionTypes";

export interface ApiRequestOptions {
    method?: string;
    headers: HeadersInit;
    body?: string;
}

export interface LoginResponse {
    user: object;
    permissions: object[];
    qboAuthUrl?: string;
}

export interface StripeCustomerListApiResult {
    id: string;
    email?: string;
    name?: string;
    business_name?: string;
    is_previous_choice?: boolean;
}

export interface QboCustomerListApiResult {
    id: string;
    email?: string;
    displayName?: string;
    companyName?: string;
    business_name?: string;
    is_previous_choice?: boolean;
}

export interface InvoiceListApiObject {
    invoice: Invoice;
    fpInvoice: FpInvoice;
    subscription_from_fp_invoice: Subscription;
}

export let ApiUtility = {
    login: function (username: string, password: string): Promise<LoginResponse> {
        return new Promise(function (resolve, reject) {
            fetch(
                '/api/users/login',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        username: username,
                        password: password
                    })
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 401 || rawResponse.status === 404 || rawResponse.status === 500) {
                        throw new Error((rawResponse.statusText));
                    } else {
                        return rawResponse.json();
                    }
                })
                .then(function (responseData) {
                    resolve(responseData);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    apiRequest: function (url: string, options?: ApiRequestOptions): Promise<any> {
        // fetch from API
        let extendedHeaders = (options && options.headers) || {};
        return new Promise(function (resolve, reject) {
            fetch(
                url,
                {
                    ...(options || {}),
                    headers: extendedHeaders
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 401) {
                        window.location.assign('/login');
                    } else if (rawResponse.status === 404 || rawResponse.status === 500) {
                        throw new Error((rawResponse.statusText));
                    } else {
                        return rawResponse.json();
                    }
                })
                .then(function (responseData) {
                    resolve(responseData);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    logout: function () {
        return ApiUtility.apiRequest(`/api/users/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        })
    },

    getIsPersonEmailAvailable: function (email: string) {
        return ApiUtility.apiRequest(`/api/users/is-person-email-available/${encodeURIComponent(email)}`);
    },

    getUsersList: function (pageNumber: number, searchText: string) {
        let argumentsArray = [];

        if (pageNumber) {
            argumentsArray.push({label: 'page', value: pageNumber})
        }

        if (searchText) {
            argumentsArray.push({label: 'searchText', value: searchText})
        }

        return ApiUtility.apiRequest(
            `/api/users${UrlUtility.getUrlQueryString(argumentsArray)}`
        );
    },

    getUser: function (userId: number) {
        return ApiUtility.apiRequest(`/api/users/${userId}`);
    },

    createUser: function (userData: object) {
        return ApiUtility.apiRequest(
            '/api/users/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData),
            }
        );
    },

    updateUser: function (userData: GenericModel) {
        return ApiUtility.apiRequest(
            `/api/users/${userData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            }
        );
    },

    getRolesList: function () {
        return ApiUtility.apiRequest('/api/roles');
    },

    getDefaultInvoiceSettingsUrl: function () {
        return ApiUtility.apiRequest('/api/invoices/get-default-invoice-settings-url')
    },

    getInvoicesList: function (
        pageNumber: number,
        searchText: string,
        filterStartDateString: string,
        filterEndDateString: string,
        filterFpClientId?: number
    ): Promise<InvoiceListApiObject[]> {
        let argumentsArray = [];

        if (pageNumber) {
            argumentsArray.push({label: 'page', value: pageNumber})
        }
        if (searchText) {
            argumentsArray.push({label: 'searchText', value: searchText})
        }
        if (filterStartDateString) {
            argumentsArray.push({label: 'filterStartDateString', value: filterStartDateString})
        }
        if (filterEndDateString) {
            argumentsArray.push({label: 'filterEndDateString', value: filterEndDateString})
        }
        if (filterFpClientId) {
            argumentsArray.push({label: 'filterFpClientId', value: filterFpClientId})
        }

        return ApiUtility.apiRequest(`/api/invoices${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getInvoice: function (invoiceId: number) {
        return ApiUtility.apiRequest(`/api/invoices/${invoiceId}`);
    },

    getInvoiceByFpId: function (fpInvoiceId: number) {
        return ApiUtility.apiRequest(`/api/invoices/by-ext-fp-invoice-id/${fpInvoiceId}`);
    },

    getFpInvoiceForSubscription: function (fpInvoiceId: number) {
        return ApiUtility.apiRequest(`/api/invoices/fp-invoice-for-subscription/${fpInvoiceId}`);
    },

    billInvoice: function (invoiceData: object) {
        return ApiUtility.apiRequest(
            '/api/invoices/bill/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(invoiceData)
            }
        );
    },

    cancelInvoice: function (invoiceId: number) {
        return ApiUtility.apiRequest(
            `/api/invoices/cancel/${invoiceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    syncInvoice: function (invoiceId: number) {
        return ApiUtility.apiRequest(
            `/api/invoices/sync/${invoiceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    getQboAccountsList: function () {
        return ApiUtility.apiRequest('/api/qbo-accounts');
    },

    getFpCompaniesList: function () {
        return ApiUtility.apiRequest('/api/fp-companies');
    },

    getFpServiceTypesList: function () {
        return ApiUtility.apiRequest('/api/fp-service-types');
    },

    getFpServicesForType: function (fpServiceTypeId: number) {
        return ApiUtility.apiRequest(`/api/fp-services/for-service-type/${fpServiceTypeId}`);
    },

    updateFpServicesForType: function (fpServicesData: object, fpServiceTypeId: number) {
        return ApiUtility.apiRequest(
            `/api/fp-services/for-service-type/${fpServiceTypeId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(fpServicesData)
            }
        );
    },

    getFpServiceType: function (fpServiceTypeId: number) {
        return ApiUtility.apiRequest(`/api/fp-service-types/${fpServiceTypeId}`);
    },

    updateFpServiceType: function (fpServiceTypeData: GenericModel) {
        return ApiUtility.apiRequest(
            `/api/fp-service-types/${fpServiceTypeData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(fpServiceTypeData)
            }
        );
    },

    getInvoicePaymentSettings: function () {
        return ApiUtility.apiRequest('/api/invoice-payment-settings');
    },

    updateInvoicePaymentSettings: function (invoicePaymentSettingsData: object) {
        return ApiUtility.apiRequest(
            '/api/invoice-payment-settings',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(invoicePaymentSettingsData)
            }
        );
    },

    addTestFullStripeInvoicePayment: function (invoiceId: number) {
        return ApiUtility.apiRequest(
            `/api/invoices/add-test-full-stripe-invoice-payment/${invoiceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },


    addTestFullQboInvoicePayment: function (invoiceId: number) {
        return ApiUtility.apiRequest(
            `/api/invoices/add-test-full-qbo-invoice-payment/${invoiceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    addTestPartialQboInvoicePayment: function (invoiceId: number) {
        return ApiUtility.apiRequest(
            `/api/invoices/add-test-partial-qbo-invoice-payment/${invoiceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    getSubscriptionInvoicesList: function (pageNumber: number, searchText: string, filterStripeCustomerId?: string) {
        let argumentsArray = [];

        if (pageNumber) {
            argumentsArray.push({label: 'page', value: pageNumber})
        }

        if (filterStripeCustomerId) {
            argumentsArray.push({label: 'filterStripeCustomerId', value: filterStripeCustomerId})
        }

        return ApiUtility.apiRequest(
            `/api/subscription-invoices${UrlUtility.getUrlQueryString(argumentsArray)}`
        );
    },

    getSubscriptionsList: function (pageNumber: number, searchText: string, filterStripeCustomerId?: string) {
        let argumentsArray = [];

        if (pageNumber) {
            argumentsArray.push({label: 'page', value: pageNumber})
        }

        if (filterStripeCustomerId) {
            argumentsArray.push({label: 'filterStripeCustomerId', value: filterStripeCustomerId})
        }

        return ApiUtility.apiRequest(
            `/api/subscriptions${UrlUtility.getUrlQueryString(argumentsArray)}`
        );
    },

    createSubscription: function (subscriptionData: object) {
        return ApiUtility.apiRequest(
            '/api/subscriptions/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(subscriptionData),
            }
        );
    },

    cancelSubscription: function (subscriptionId: number) {
        return ApiUtility.apiRequest(
            `/api/subscriptions/cancel/${subscriptionId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    getStripeCustomersList: function (extFpCompanyId?: number): Promise<StripeCustomerListApiResult[]> {
        let argumentsArray = [];

        if (extFpCompanyId) {
            argumentsArray.push({label: 'extFpCompanyId', value: extFpCompanyId})
        }

        return ApiUtility.apiRequest(`/api/stripe-customers${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getQboCustomersList: function (extFpCompanyId: number): Promise<QboCustomerListApiResult[]> {
        let argumentsArray = [];

        if (extFpCompanyId) {
            argumentsArray.push({label: 'extFpCompanyId', value: extFpCompanyId})
        }

        return ApiUtility.apiRequest(`/api/qbo-customers${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getFpServicesList: function (): Promise<FpService[]> {
        return ApiUtility.apiRequest('/api/fp-services');
    },

    getSubscription: function (subscriptionId: number) {
        return ApiUtility.apiRequest(`/api/subscriptions/${subscriptionId}`);
    },

    getSubscriptionBillingPeriods: function () {
        return ApiUtility.apiRequest(`/api/subscription-billing-periods`);
    },

    syncSubscriptionInvoices: function (subscriptionId: number) {
        return ApiUtility.apiRequest(
            `/api/subscriptions/sync-invoices/${subscriptionId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    syncAllSubscriptionInvoices: function () {
        return ApiUtility.apiRequest(
            `/api/subscriptions/sync-all-invoices`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
    },

    getIsInvoiceNumberAvailable: function (invoiceNumber: string) {
        return ApiUtility.apiRequest(`/api/invoices/is-number-available/${encodeURIComponent(invoiceNumber)}`);
    },

    createStripeCustomer: function (customerData: object) {
        return ApiUtility.apiRequest(
            '/api/stripe-customers/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(customerData),
            }
        );
    },

    getIsStripeCustomerEmailAvailable: function (email: string) {
        return ApiUtility.apiRequest(`/api/stripe-customers/is-email-available/${encodeURIComponent(email)}`);
    },

    createQboCustomer: function (customerData: object) {
        return ApiUtility.apiRequest(
            '/api/qbo-customers/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(customerData),
            }
        );
    },

    getIsQboCustomerEmailAvailable: function (email: string) {
        return ApiUtility.apiRequest(`/api/qbo-customers/is-email-available/${encodeURIComponent(email)}`);
    },

    getIsQboCustomerNameAvailable: function (name: string) {
        return ApiUtility.apiRequest(`/api/qbo-customers/is-name-available/${encodeURIComponent(name)}`);
    },

    getSupportedStripePaymentTypes: function () {
        return ApiUtility.apiRequest('/api/invoices/supported-stripe-payment-types');
    }
};
